import React, { FC, useState, useEffect } from 'react';
import { deleteSenior } from '../../lib/supabase';

interface DeleteSeniorModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  onDeleted?: () => void;
}

export const DeleteSeniorModal: FC<DeleteSeniorModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  onDeleted,
}) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setDeleting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      await deleteSenior(seniorId);
      onDeleted?.();
      onClose();
    } catch (err) {
      console.error('Failed to delete senior:', err);
      setError('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-text/50 backdrop-blur-sm animate-fadeIn"
        onClick={deleting ? undefined : onClose}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center pt-6">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
            <span className="material-symbols-outlined text-3xl text-red-600">delete_forever</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h2 className="font-display text-xl font-bold text-brand-text mb-2">
            Delete {seniorName}?
          </h2>
          <p className="text-sm text-brand-text-muted">
            This will permanently remove this senior and all their configuration, contacts, and assessment data.
            This action cannot be undone.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mb-4 p-3 bg-red-50 rounded-lg border border-red-100">
            <p className="text-sm text-red-600 flex items-center justify-center gap-2">
              <span className="material-symbols-outlined text-lg">error</span>
              {error}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 py-2.5 px-4 text-sm font-semibold text-brand-text-light bg-brand-bg-warm border border-brand-border rounded-lg hover:bg-brand-border/50 hover:text-brand-text transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 py-2.5 px-4 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {deleting ? (
              <>
                <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                Deleting...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">delete</span>
                Yes, Delete
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteSeniorModal;
